import { CaseReviewIcon } from "../field_review/CaseReviewIcon";
import type { CollectionSpec } from "../../constants/caseReviewSections";
import type { CaseReviewCollectionApi } from "../../hooks/useCaseReview";
import type { CaseFieldValues } from "../../model/caseReview";
import { isNewRecordId } from "../../utils/catalogBlankRecord";
import { recordTitle } from "../../utils/caseReviewRecordTitle";
import type { ReviewCounts } from "../../model/caseReview";
import { ReviewCounter } from "../review_counts/ReviewCounter";

/**
 * One chip per record of a collection: pick a chip, the table below shows that record.
 *
 * <p>The whole of a collection's identity lives here — its title, what it is, how far through it
 * the reviewer is — so the table below can be nothing but fields. It is the same strip whichever
 * collection asked and at either level of nesting, because a reviewer choosing between two
 * reporters and a reviewer choosing between two dosage regimens are doing the same thing.
 */

interface Props {
  api: CaseReviewCollectionApi;
  /** A coded value's label, so a chip says "Suspect" rather than "2". */
  optionLabel: (values: CaseFieldValues) => (property: string) => string | undefined;
  counts: (recordId: string) => ReviewCounts;
  enabled: boolean;
  showDisabledAdd?: boolean;
}

/** The chip's own line under the title: what this record is, in the reviewer's words. */
function subtitleOf(spec: CollectionSpec, values: CaseFieldValues,
                    optionLabel: (property: string) => string | undefined,
                    recordId: string, isNew: boolean, unsaved: number): string | undefined {
  const tags = [...(spec.tags?.(values, optionLabel, recordId) ?? [])];
  if (isNew) tags.push("New");
  if (unsaved > 0) tags.push(`${unsaved} unsaved`);
  return tags.length ? tags.join(" · ") : undefined;
}

export function RecordStrip({ api, optionLabel, counts, enabled, showDisabledAdd = false }: Props) {
  const { spec } = api;
  return <div className="cr-strip" role="tablist" aria-label={spec.labels.records}>
    {api.rows.map((row, index) => {
      const on = row.recordId === api.selectedId;
      const label = optionLabel(row.values);
      const title = recordTitle(spec, row.values, index, label, row.recordId);
      const progress = counts(row.recordId);
      const subtitle = subtitleOf(spec, row.values, label, row.recordId, isNewRecordId(row.recordId), progress.unsaved);
      return <div key={row.recordId} className="cr-record-chip-group">
        <button type="button" role="tab" aria-selected={on} data-on={on} className="cr-record-chip"
          onClick={() => api.select(row.recordId)}>
          <span className="cr-record-chip-stack">
            <span className="cr-record-chip-title">{title}</span>
            {subtitle && <span className="cr-record-chip-subtitle">{subtitle}</span>}
          </span>
          <ReviewCounter label={title} counts={progress} />
        </button>
        {/* Only on the chosen chip: deleting a record you cannot see is how the wrong one goes. */}
        {enabled && spec.canRemove !== false && on && <button type="button" className="cr-record-chip-delete"
          aria-label={`${spec.labels.remove} ${index + 1}`} onClick={() => api.remove(row.recordId)}>
          <CaseReviewIcon name="trash" />
        </button>}
      </div>;
    })}
    {(enabled || showDisabledAdd) && spec.canAdd !== false && <button type="button" className="cr-record-chip-add" disabled={!enabled} onClick={api.add}>
      <CaseReviewIcon name="plus" />{spec.labels.add}
    </button>}
  </div>;
}
